// ─────────────────────────────────────────────
//  RoomStatusScreen — the container for Room Status.
//
//  The two pages (MobileRoomStatus, SupervisorRoomStatus) are presentational
//  and take every value as a prop; this is the only file that knows about
//  Firestore, contexts and hooks. Same split as MobileRoomCard, which "renders
//  and delegates; it never touches Firestore".
//
//  ⚠️ WHICH PAGE YOU GET IS DECIDED PER ROOM, NOT BY ROLE. A solo
//  anaesthesiologist is the OCCUPANT of their room and needs the stamp
//  buttons; keying the choice on `isDoctor` would hand them the
//  acknowledge-only screen and leave them unable to record their own case.
//  domain/caseRole resolves it from the LIVE assignment so a lunch relief also
//  gets the buttons.
// ─────────────────────────────────────────────
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useMessaging } from "../../context/MessagingContext";
import { useAlertCenter, alertsCoverFacility } from "../../context/AlertContext";
import { useDayKey } from "../../hooks/useDayKey";
import { useMyRooms } from "../../hooks/useMyRooms";
import { useTasks } from "../../hooks/useTasks";
import useRoomCases, { minutesSince } from "../../hooks/useRoomCases";
import useCasePhases from "../../hooks/useCasePhases";
import { caseWriterMode, currentAttendingId, isSoloCase } from "../../domain/caseRole";
import { userStaffIds } from "../../domain/myRooms";
import { CHANNEL_KINDS } from "../../domain/channels";
import {
  normaliseRoomMessages,
  roomMessageBoxState,
} from "../../domain/roomMessageBox";
import MobileRoomStatus from "./MobileRoomStatus";
import SupervisorRoomStatus from "./SupervisorRoomStatus";
import RoomMessageBox from "./RoomMessageBox";
import MobileAlertButtons from "./MobileAlertButtons";
import RoomStatusBreaks from "./RoomStatusBreaks";
import useRoomBreakActions from "../../hooks/useRoomBreakActions";

export default function RoomStatusScreen() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const facilityId = user?.activeFacilityId || null;
  const dateKey = useDayKey();

  // ⚠️ `staff` RIDES ALONG DELIBERATELY — useMyRooms exposes it so consumers do
  // not open a second staff listener, and this screen needs it to answer the
  // same identity question useMyRooms already answered.
  const { myCards, staff } = useMyRooms(facilityId, dateKey);

  // The viewer's STAFF ids, which is how an assignment names people —
  // `assignment.ahpId` is a `staff/{id}`, not an auth uid. Passing the bare uid
  // to caseWriterMode compared two different id spaces, so nothing ever matched
  // and every Room Status screen rendered read-only: the CRNA could not stamp a
  // phase and the supervisor could not acknowledge one. `userStaffIds` is the
  // resolver useMyRooms uses to decide the room is "mine" in the first place,
  // so using it here makes the two answers agree.
  const myStaffIds = useMemo(() => userStaffIds(user, staff), [user, staff]);
  const { openCaseFor, loading: casesLoading, error: casesError } =
    useRoomCases(facilityId, dateKey);
  const { stampPhase, clearPhase, stampBoundary, acknowledgePhase, startNewCase } =
    useCasePhases();

  const [busy, setBusy] = useState(false);

  // ── WHO AM I IN EACH ROOM ────────────────────────────────────────────
  const cards = useMemo(
    () =>
      (myCards || []).map((c) => ({
        key: c.key,
        room: c.room,
        assignment: c.assignment,
        orCase: openCaseFor(c.room?.id),
        surgeon: c.assignment?.surgeon || c.room?.surgeon || "",
        mode: caseWriterMode({ uid, staffIds: myStaffIds, assignment: c.assignment, room: c.room }),
      })),
    [myCards, openCaseFor, uid, myStaffIds]
  );

  // A room I am actually running gets the buttons. Otherwise this is the
  // supervisor's all-rooms page.
  const myOwnCard = cards.find((c) => c.mode === "provider") || null;

  // ── THE MESSAGE BOX ──────────────────────────────────────────────────
  const { channels, messagesByChannel, messagingFacilityId, messagesDegraded, messagesError } =
    useMessaging();
  const { alerts, alertFacilityId } = useAlertCenter();
  const { tasks, loading: tasksLoading, error: tasksError } = useTasks(facilityId, dateKey);

  // ⚠️ BOTH BUSES MUST COVER THIS CAMPUS. Alerts and messaging each follow the
  // signed-in facility independently, and a per-room channel id embeds the
  // facility — so a foreign room simply never matches and would look quiet
  // rather than unavailable. Refuse unless both agree, and fail closed.
  const covered =
    alertsCoverFacility(alertFacilityId, facilityId) && messagingFacilityId === facilityId;

  const channelIdForRoom = useCallback(
    (roomId) =>
      (channels || []).find(
        (ch) => ch.kind === CHANNEL_KINDS.perOr && ch.scopeId === roomId
      )?.id || null,
    [channels]
  );

  const boxRooms = myOwnCard ? [myOwnCard] : cards;

  const messages = useMemo(() => {
    const out = [];
    for (const c of boxRooms) {
      const chId = channelIdForRoom(c.room?.id);
      out.push(
        ...normaliseRoomMessages({
          channelMessages: (chId && messagesByChannel?.[chId]) || [],
          tasks: tasks || [],
          alerts: alerts || [],
          room: c.room,
        })
      );
    }
    return out.sort((a, b) => {
      if (!a.at && !b.at) return 0;
      if (!a.at) return -1;
      if (!b.at) return 1;
      return a.at - b.at;
    });
  }, [boxRooms, channelIdForRoom, messagesByChannel, tasks, alerts]);

  const boxState = roomMessageBoxState({
    covered,
    degraded: messagesDegraded,
    error: messagesError || tasksError,
    loading: tasksLoading,
    messages,
  });

  const navigate = useNavigate();

  // ── THE BREAK SYSTEM FOR MY OWN ROOM ─────────────────────────────────────
  // Owner, 2026-09-07: "add a break system in the room status page before the
  // message box."
  //
  // ⚠️ THE SAME HOOK THE ROOM CARD USES, and the same identity resolution the
  // rest of this screen uses — `myStaffIds`, not a bare uid. Without it
  // `iAmRoomProvider` is false for every real clinician (a room names people by
  // `staff/{id}`), so the controls would never appear for the one person
  // entitled to press them.
  const breakRoom = myOwnCard?.room || null;
  const breakActions = useRoomBreakActions({
    room: breakRoom || { id: null, name: "", breaks: null },
    assignment: myOwnCard?.assignment || null,
    providerName: user?.name || "",
    staffIds: myStaffIds,
  });

  const breakBox = breakRoom ? (
    <RoomStatusBreaks
      room={breakRoom}
      canRequest={breakActions.iAmRoomProvider}
      // Anyone on the roster can go and cover somebody else's room; the break
      // board is the surface that knows which rooms are waiting.
      canGive
      requestPending={Boolean(breakActions.breakRequested)}
      busy={busy}
      onRequest={(channel) => withBusy(() => breakActions.requestMyBreak(channel))}
      // Only the room's own provider may call off their request, and the hook
      // refuses once a cover has been named — passing the handler only when
      // they may press it keeps a refusing control off the screen.
      onCancelRequest={
        breakActions.iAmRoomProvider
          ? () => withBusy(() => breakActions.cancelMyBreakRequest())
          : null
      }
      // ⚠️ NAVIGATES RATHER THAN MOUNTING THE SHEET. GiveBreakSheet needs the
      // rooms, roster, assignments and locations of the whole facility, which
      // this screen does not load — and the break board is already the surface
      // for covering somebody. Sending people to the working screen beats
      // re-plumbing its data into a second one.
      onGive={() => navigate("/breaks")}
    />
  ) : null;

  const messageBox = (
    <RoomMessageBox state={boxState} messages={messages} showRoomTag={!myOwnCard} />
  );

  // ── ACTIONS ──────────────────────────────────────────────────────────
  const withBusy = useCallback(async (fn) => {
    setBusy(true);
    try {
      return await fn();
    } finally {
      setBusy(false);
    }
  }, []);

  // ⚠️ STAFF ID IN, AUTH UID OUT — and the conversion has to happen HERE.
  // An assignment names the supervising doctor by `staff/{id}`; push tokens live
  // on `users/{uid}`. Handing a staff id to a directive writes a clean document
  // that no phone ever rings for, which is the failure domain/tasks warns about
  // in those words. `staff.uid` is the link claimStaffLink writes.
  //
  // An unlinked doctor resolves to nothing, and that is correct: the phase is
  // still recorded, and nobody is told a message was delivered when it was not.
  const supervisorUidsFor = useCallback(
    (assignment, room) => {
      const attendingStaffId = currentAttendingId(assignment, room);
      if (!attendingStaffId) return [];
      const doc = (staff || []).find((x) => x && x.id === attendingStaffId);
      return doc?.uid ? [doc.uid] : [];
    },
    [staff]
  );

  const ageFor = useCallback(
    (orCase, phaseId) => minutesSince(orCase?.phases?.[phaseId]?.at),
    []
  );

  // ── RENDER ───────────────────────────────────────────────────────────
  if (myOwnCard) {
    const { room, orCase, surgeon, assignment } = myOwnCard;
    return (
      <MobileRoomStatus
        room={room}
        orCase={orCase}
        mode="provider"
        surgeon={surgeon}
        busy={busy}
        // The emergency system already in place — same component MobileRoomCard
        // mounts, so the two surfaces cannot come to disagree about what
        // raising an emergency does.
        // ⚠️ NO URGENCY IN A SOLO CASE. Urgency means "tell my supervising
        // doctor" — it is raised BY the person in the room and delivered TO the
        // anaesthesiologist directing it. When the same human is both
        // (`isSoloCase`: providerId === attendingId) there is nobody to
        // escalate to, so the control is not merely redundant, it is a button
        // that pages you about yourself. Owner, 2026-09-07.
        //
        // Emergency stays, and MobileAlertButtons centres it on its own
        // (`gridSolo`) — an emergency is a call to the whole floor, not to one
        // doctor, so a solo anaesthetist needs it more than anyone.
        alertButtons={
          <MobileAlertButtons
            room={room}
            assignment={assignment}
            showUrgency={!isSoloCase(assignment, room)}
            compact
          />
        }
        breakBox={breakBox}
        ageMinutesFor={(phaseId) => ageFor(orCase, phaseId)}
        onStamp={(phaseId) =>
          withBusy(() =>
            stampPhase({
              orCase, phaseId, room, uid, userName: user?.name || "",
              assignment,
              supervisorUids: supervisorUidsFor(assignment, room),
              facilityId,
              dateKey,
            })
          )
        }
        // ⚠️ THE SECOND TAP CLEARS, IT DOES NOT RE-STAMP. Owner, 2026-09-11:
        // "If you click again the same button will clear that time." No
        // supervisor directive goes out for a clear: the phase task announces
        // "Induction 07:41", and there is nothing to announce about its
        // removal — the row simply goes back to unstamped on their screen too.
        onClear={(phaseId) =>
          withBusy(() => clearPhase({ orCase, phaseId, uid }))
        }
        onBoundary={(eventId) =>
          withBusy(() => stampBoundary({ orCase, eventId, room, uid }))
        }
        onAck={(phaseId) =>
          withBusy(() =>
            acknowledgePhase({ orCase, phaseId, uid, userName: user?.name || "" })
          )
        }
        onNewCase={() =>
          withBusy(() =>
            startNewCase({
              orCase, room, assignment, facilityId, date: dateKey, uid, surgeon,
            })
          )
        }
        messageBox={messageBox}
      />
    );
  }

  return (
    <SupervisorRoomStatus
      rooms={cards}
      busy={busy}
      loading={casesLoading}
      error={casesError}
      ageMinutesFor={(roomId, phaseId) =>
        ageFor(cards.find((c) => c.room?.id === roomId)?.orCase, phaseId)
      }
      onAck={(roomId, phaseId) => {
        const card = cards.find((c) => c.room?.id === roomId);
        if (!card?.orCase) return undefined;
        return withBusy(() =>
          acknowledgePhase({
            orCase: card.orCase, phaseId, uid, userName: user?.name || "",
          })
        );
      }}
      messageBox={messageBox}
    />
  );
}
